import React from "react";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Logo — the ForgedOps Plans brand mark.
 *
 * Variants:
 *  - "full" (default): horizontal lock-up — emblem + "FORGEDOPS PLANS" wordmark.
 *    Used in login screen, sidebar, share portal, public/marketing surfaces.
 *  - "mark": emblem-only (steel F + orange forge ember). Used in tight spaces
 *    where the wordmark would be illegible: Field Mode top bar, favicon, PWA
 *    icon, hover badges, super-compact headers.
 *
 * The official logo IS a wordmark — it includes "FORGEDOPS PLANS" baked into
 * the artwork. So when we display the full variant, we DO NOT also render a
 * text label next to it. The legacy `showText` prop is preserved only for
 * tenants who upload a non-wordmark logo (just a mark / monogram).
 *
 * Tenant override: if `tenant.branding.logo_url` is set, that takes precedence
 * over the master ForgedOps brand asset (white-label support for share portal
 * + PDF exports). The default ForgedOps tenant has logo_url=null so it falls
 * through to the bundled master asset.
 */

const MASTER_FULL = "/brand/forgedops-plans-logo.png";
const MASTER_MARK = "/brand/forgedops-plans-mark.png";

export default function Logo({
  size = "md",
  variant = "full",  // "full" | "mark"
  showText = true,    // legacy: only relevant when a non-master tenant logo lacks a wordmark
  className = "",
  ...rest
}) {
  const { tenant } = useTenant();
  const hasTenantOverride = Boolean(tenant?.branding?.logo_url);
  const isMark = variant === "mark";

  // Heights chosen to feel correctly weighted next to neighboring chrome.
  // Full lock-up: master art is roughly 3.2:1 aspect, so a 32px-tall image is
  // ~100px wide — a comfortable sidebar header size.
  const dims = isMark
    ? (size === "sm" ? "h-5" : size === "lg" ? "h-9" : "h-7")
    : (size === "sm" ? "h-7" : size === "lg" ? "h-12" : "h-9");

  const src = hasTenantOverride
    ? tenant.branding.logo_url
    : (isMark ? MASTER_MARK : MASTER_FULL);

  const alt = tenant?.display_name || "ForgedOps Plans";
  // Suppress the side text label when the image already IS a wordmark — that's
  // the master full lock-up OR an unknown tenant logo on which we have to
  // assume the worst (legacy `showText=true` default).
  const wordmarkBakedIn = !hasTenantOverride && !isMark;

  return (
    <div className={`flex items-center gap-2.5 ${className}`} data-testid="brand-logo" {...rest}>
      <img
        src={src}
        alt={alt}
        className={`${dims} w-auto object-contain select-none`}
        draggable={false}
      />
      {showText && !wordmarkBakedIn && (
        <div className="leading-tight">
          <div className={`font-display font-semibold ${size === "sm" ? "text-sm" : size === "lg" ? "text-xl" : "text-base"} text-[var(--fop-text)]`}>
            {tenant?.display_name || "ForgedOps Plans"}
          </div>
          {size !== "sm" && tenant?.branding?.tagline && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] font-mono">
              {tenant.branding.tagline}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
